import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton,
  Autocomplete, TextField, Box, Typography, CircularProgress, MenuItem,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useGetEntityNetwork } from '../../api/entities';
import { useGetTransactionSource, useSetTransactionSource } from '../../api/transactions';
import { formatCurrency, formatPercentage } from '../../utils/formatters';

// Account-ownership Phase 2: tag a single transaction's source of funds to one
// or more entities — (:Transaction)-[:OWNED_BY {pct}]->(:Entity). Whole-number
// percents in the form → 0-1 fractions to the API. The untagged remainder is NOT
// asserted here; the Phase 3 source calculation sources it. Mirrors the
// CategoryEditDialog interaction model (MUI Dialog) for consistency.
//
// A Person CAN be a source owner (source attributes funds; it is not a :OWNS cap
// table where a person can't be owned).
export default function SourceEditDialog({ open, onClose, onSaved, transactionId, amount }) {
  const { data: network } = useGetEntityNetwork(open);
  const entities = useMemo(() => network?.entities || [], [network]);
  const entitiesById = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
  const { data: current, isLoading } = useGetTransactionSource(transactionId, open);
  const setSource = useSetTransactionSource();

  const [rows, setRows] = useState([]); // [{ entity_id, pct }] — pct is whole-number 0-100
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Seed the rows from the transaction's current tags when the dialog opens.
  useEffect(() => {
    if (!open) return;
    const owners = current?.owners || [];
    setRows(owners.length
      ? owners.map((o) => ({ entity_id: o.entity_id, pct: Math.round((Number(o.pct) || 0) * 100) }))
      : [{ entity_id: '', pct: '' }]);
    setErr(null);
  }, [open, current]);

  const totalPct = rows.reduce((s, r) => s + (Number(r.pct) || 0), 0);
  const untagged = Math.max(0, 100 - totalPct);
  const absAmount = Math.abs(Number(amount) || 0);

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, { entity_id: '', pct: '' }]);
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : [{ entity_id: '', pct: '' }]));

  const validationError = () => {
    if (totalPct > 100.0001) return 'Source can’t exceed 100%.';
    const ids = rows.filter((r) => r.entity_id).map((r) => r.entity_id);
    if (new Set(ids).size !== ids.length) return 'An entity is listed more than once.';
    for (const r of rows) {
      if (r.entity_id && !(Number(r.pct) > 0)) return 'Each tagged entity needs a percent greater than 0.';
    }
    return null;
  };

  const handleSave = async () => {
    const v = validationError();
    if (v) { setErr(v); return; }
    const owners = rows
      .filter((r) => r.entity_id && Number(r.pct) > 0)
      .map((r) => ({ entity_id: r.entity_id, pct: Number(r.pct) / 100 }));
    setBusy(true); setErr(null);
    try {
      await setSource(transactionId, owners); // empty owners clears all tags
      onSaved?.(); // revalidate the enriched list so the inline Source column refreshes
      onClose();
    } catch (e) {
      setErr((e && (e.message || e.toString())) || 'Couldn’t save the source tags.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Source of funds — this transaction</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Who do these funds belong to? Any remainder stays <strong>untagged</strong> — its
          source is determined by the source calculation, not asserted here.
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {rows.map((r, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1.5 }}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={entities}
                  getOptionLabel={(o) => (typeof o === 'string' ? o : (o.display_name || ''))}
                  isOptionEqualToValue={(o, v) => o.id === (v?.id || v)}
                  value={entitiesById[r.entity_id] || null}
                  onChange={(e, val) => setRow(i, { entity_id: val?.id || '' })}
                  renderOption={(props, o) => (
                    <MenuItem {...props} key={o.id}>
                      {o.display_name}
                      <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{o.kind}</Typography>
                    </MenuItem>
                  )}
                  renderInput={(params) => <TextField {...params} label="Entity" placeholder="Select or search…" size="small" />}
                />
                <TextField
                  label="%" type="number" size="small" sx={{ width: 110 }}
                  value={r.pct}
                  onChange={(e) => setRow(i, { pct: e.target.value })}
                  inputProps={{ min: 0, max: 100 }}
                  helperText={r.pct && absAmount ? formatCurrency(absAmount * (Number(r.pct) / 100)) : ' '}
                />
                <IconButton aria-label="Remove entity" size="small" sx={{ mt: 0.5 }} onClick={() => removeRow(i)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            <Button startIcon={<AddIcon />} size="small" onClick={addRow}>Add entity</Button>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
              <Typography variant="body2">Tagged: {formatPercentage(totalPct / 100)}</Typography>
              <Typography variant="body2" color="text.secondary">Untagged: {formatPercentage(untagged / 100)}</Typography>
            </Box>
            {err && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{err}</Typography>}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={busy || isLoading}>{busy ? 'Saving…' : 'Save'}</Button>
      </DialogActions>
    </Dialog>
  );
}
